// Every intent the DOM chrome can send, as the one `UIActions` object the
// overlay is built with (plan C.2 `fast/actions.ts`).
//
// TypeScript enforces the completeness: `UIActions` names every key, so a
// missing one is a compile error and nobody has to keep a checklist. That
// is why the whole object lived here from milestone M1, when the map was
// the only thing on the glass: the later milestones filled a body in, and
// none of them widened this file's reach.
//
// Since milestone M4 every body is real. No key is a stub, and none may
// become one again: a dock tab that the engine cannot answer must refuse
// through `refuse`, which the player sees, and not through a console line
// nobody reads.

import { authLogout, postReport, putBlock } from '../net/api';
import { ADVISOR_KEY } from '../ui/advisor';
import { WELCOME_KEY } from '../ui/welcome';
import { storeAdvisor } from '../ui/settings';
import type { AudioDirector } from '../audio/director';
import type { ClanChatConnection } from '../net/clanchat';
import type { Connection } from '../net/connection';
import { clearToken } from '../net/token';
import { assignWithGesture } from '../ui/nav';
import { ARCADE_HREF } from '../ui/hublink';
import type { TabKey } from '../ui/hub/panel';
import type { EventBus } from '../events';
import { heatMode, heatServerKind, type GameState } from '../state';
import type { AudioOp, Overlay, UIActions } from '../ui/overlay';
import { soundSettings } from '../ui/themestate';
import { storeOf } from '../ui/localstore';
import type { Camera } from '../render/camera';
import { cellCenter } from '../world';
import { CHART } from '../gen/presentation';
import { shippingLanes } from './chart/pieces/shipping-lanes';
import { rememberSeaSocket, seaViewHref, withAtKnob } from './sealink';
import {
  ChatChannel, HeatmapKind, JobKind, ReportKind, type ShipClass,
} from '../gen/proto/aquaphobia';
import { main as S } from '../gen/strings';
import type { HeatmapLayer } from './layers/heatmap';
import type { Commands } from './commands';

/** What `makeActions` needs. The late-built members are read, never held. */
export interface ActionDeps {
  conn: () => Connection;
  ui: () => Overlay;
  state: GameState;
  camera: Camera;
  bus: EventBus;
  heatmap: HeatmapLayer;
  /** The one owner of sound on this page. */
  audio: AudioDirector;
  /** Pushes what the director published back to the settings and media cards. */
  pushAudio: () => void;
  /**
   * The clan's room on the meta server, or null in direct mode. It is dialled
   * after the overlay, which is built from this object, so it is read.
   */
  clanChat: () => ClanChatConnection | null;
  /**
   * The map's decisions (M2). It is built after the overlay, which is built
   * from this object, so it is read and never held.
   */
  commands: () => Commands;
  /**
   * Takes the world down before the page leaves it (R1203): the game
   * socket, the clan chat socket and the engine's frame loop. MENU calls it
   * right before it navigates.
   */
  leave: () => void;
  /** Where the fleet layer draws a hull now, or null for a hull it does
   *  not hold: SEA VIEW HERE opens at the selected fleet (R2493). */
  fleetAt: (fleetId: number) => { x: number; y: number } | null;
  /** The mode word of this world from the join answer, or '' for a
   *  direct dial: SEA VIEW HERE names it in `mode=` (R2828). */
  worldMode?: () => string;
}

/**
 * The hub address of this page (R1203): the page itself with `world` and
 * `server` taken off. `world` would rejoin the world the player is leaving
 * and `server` would connect direct, and `resolveEntry` reads both before
 * it reaches the hub. Every other parameter stays. A `tab` opens the hub on
 * that section, the way a sign-in provider's return does (R621): the guest
 * warning's REGISTER opens SETTINGS (R1565).
 */
export function hubAddress(href: string, tab?: TabKey): string {
  const url = new URL(href);
  url.searchParams.delete('world');
  url.searchParams.delete('server');
  if (tab !== undefined) url.searchParams.set('tab', tab);
  return url.toString();
}

/**
 * The media player's buttons, one op each. They arrive as one op, the way
 * `fleetOp` and `admin` already read.
 */
const AUDIO_OPS: Record<AudioOp, (audio: AudioDirector, arg: number | undefined) => void> = {
  play: (a) => a.musicPlay(),
  pause: (a) => a.musicPause(),
  stop: (a) => a.musicStop(),
  next: (a) => a.musicNext(),
  prev: (a) => a.musicPrev(),
  shuffle: (a, arg) => a.musicShuffle(!!arg),
  jump: (a, arg) => a.musicJump(arg ?? 0),
};

export function makeActions(deps: ActionDeps): UIActions {
  const { conn, ui } = deps;

  /**
   * Takes the world down and goes to another page (R1203): MENU, ARCADE and
   * a hub row of ☸ OPTIONS. A throw in the teardown must never keep the
   * player in the world, so the navigation runs whatever it says.
   * `assignWithGesture` carries the press, so the menu music may start on
   * the next page.
   */
  const leaveFor = (href: string): void => {
    try { deps.leave(); } catch (err) { console.warn('[leave] teardown', err); }
    assignWithGesture(href);
  };

  return {
    // -- the build queue and the yard --------------------------------------
    build: (id) => conn().sendEnqueue(JobKind.JOB_KIND_BUILDING, id),
    buildShips: (cls, n) => conn().sendEnqueue(JobKind.JOB_KIND_SHIP, cls, n),
    buildTorpedoes: (kind, n) => conn().sendEnqueue(JobKind.JOB_KIND_TORPEDO, kind, n),
    orderSeaObjects: (kind, n) => conn().sendEnqueue(JobKind.JOB_KIND_SEA_OBJECT, kind, n),
    research: (id) => conn().sendEnqueue(JobKind.JOB_KIND_RESEARCH, id),
    cancelJob: (id) => conn().sendJobAction(id, true),
    rushJob: (id) => conn().sendJobAction(id, false),
    moveJob: (id, dir) => conn().sendJobAction(id, false, dir),
    repair: (id) => conn().sendEnqueue(JobKind.JOB_KIND_REPAIR, id),

    // -- the dock ----------------------------------------------------------
    // Every deploy goes through `Commands.createFleet`, which arms the wait
    // that hands the selection to the hull the server makes (R90).
    deploy: (cls: ShipClass, n, captainId) => deps.commands().createFleet(cls, n, captainId),
    // A deploy names the penned hull: the pens hold fleet objects, so the
    // first argument is a fleet id and not a design.
    deployDesign: (fleetId) => deps.commands().createFleet(0, fleetId),
    invent: (hull, modules) => conn().sendInvent(
      hull, modules, ui().inventWeapType, ui().inventShieldType),
    print: (master, runs) => conn().sendDesignAction({ design: master, printRuns: runs }),
    scrapDesign: (id) => conn().sendDesignAction({ design: id, scrap: true }),
    installPrint: (design) => conn().sendDesignAction({ design, install: true }),
    redraft: (design, slot) => conn().sendDesignAction({ design, redraft: true, redraftSlot: slot }),
    useDataCore: () => conn().sendDesignAction({ useDataCore: true }),
    market: (a) => conn().sendMarket(a),
    // The casino (casino.md §5). One union, one sender: the panel names
    // the intent and the socket wraps it in the right oneof arm.
    casino: (a) => conn().sendCasino(a),
    expedition: (depth, captainId) => conn().sendExpedition(depth, captainId),

    // -- captains ----------------------------------------------------------
    recruit: (id) => conn().sendCaptainAction(id, { recruit: true }),
    dismiss: (id) => conn().sendCaptainAction(id, { dismiss: true }),
    ransom: (id) => conn().sendCaptainAction(id, { ransom: true }),
    releaseCaptain: (id, owner) => conn().sendCaptainAction(id, { release: true, ownerId: owner }),
    train: (captainId, attr) => conn().sendEnqueue(JobKind.JOB_KIND_TRAINING, captainId, attr),

    // -- the fleet --------------------------------------------------------
    fleetOp: (id, op, arg = 0) => deps.commands().fleetOp(id, op, arg),
    setLoad: (id, mix) => deps.commands().setLoad(id, mix),
    setStow: (id, stow) => conn().sendStow(id, stow),
    siege: (op, siteId, fleetId) => conn().sendSiegeAction(op, siteId, fleetId),
    gatherAt: (fleetId, cell) => deps.commands().gatherAt(fleetId, cell),
    setMission: (id, mission) => deps.commands().setMission(id, mission),
    selectFleet: (id) => deps.commands().selectFleet(id),
    selectSite: (id) => deps.commands().select({ kind: 'site', id }),
    aim: (fleetId) => deps.commands().toggleAim(fleetId),
    // The two ways out of something, as on-screen controls: a touchscreen
    // has neither an esc key nor a right mouse button.
    cancelModes: () => { deps.commands().cancelModes(); },
    deselect: () => deps.commands().select(null),

    // -- the control groups (R539) ----------------------------------------
    // Both halves are the map's decision, so both go through `Commands`:
    // the key press and the finger's button reach the same method, and the
    // gates cannot drift apart.
    assignGroup: (slot) => deps.commands().assignGroup(slot),
    setGroup: (slot, fleetId) => deps.commands().setGroup(slot, fleetId),
    releaseGroup: (slot) => deps.commands().releaseGroup(slot),
    recallGroup: (slot) => deps.commands().recallGroup(slot),
    // The live rename (R1250). The server is the judge of the name and
    // of the key, so the intent goes straight to the wire.
    renameFleet: (fleetId, name) => conn().sendRenameFleet(fleetId, name),
    // The naming sheet of a seamount (R2626): the server judges the name.
    nameSeamount: (n) => conn().sendNameSeamount(n),
    // A course and the course plots (v2 §7, R2589). The course plays the
    // acknowledgement of a plotted course through the commands.
    sendCourse: (fleetId, legs) => deps.commands().course(fleetId, legs),
    plotAction: (op, a) => conn().sendPlot(op, a),
    // ASSIGN of a commission (v2 §10, R2645): the hub judges the fleet.
    assignCommission: (id, fleetId) => conn().sendCommission(id, fleetId),
    // DEPOSIT and NOTE of the cache sheet (v2 §9.3, R2649): the hub judges them.
    cacheAction: (a) => conn().sendCache(a),
    // SEARCH of the SEARCH picker (R2699): the order goes through the
    // commands, as SEARCH HERE of the peek does.
    searchAt: (fleetId, cell) => deps.commands().searchAt(fleetId, cell),
    searchPoint: (fleetId, x, y) => deps.commands().searchPoint(fleetId, x, y),
    // REPORT (deployment.md §7.6, R1323). A game-server piece goes over
    // the socket and gets no answer; a clan chat line and a block go to
    // the meta server, which answers.
    report: (t, reason, note) => {
      switch (t.kind) {
        case 'line': conn().sendReport(ReportKind.REPORT_KIND_CHAT, { lineId: t.lineId }, reason, note); break;
        case 'commander': conn().sendReport(ReportKind.REPORT_KIND_COMMANDER, { accountId: t.accountId }, reason, note); break;
        case 'fleet': conn().sendReport(ReportKind.REPORT_KIND_FLEET, { fleetId: t.fleetId }, reason, note); break;
        case 'seamount': conn().sendReport(ReportKind.REPORT_KIND_SEAMOUNT, { body: t.body }, reason, note); break;
        case 'cache': conn().sendReport(ReportKind.REPORT_KIND_CACHE_NOTE, { cache: t.cache }, reason, note); break;
      }
    },
    reportClanLine: async (seq, reason, note) => { await postReport({ kind: 'chat', seq }, reason, note); },
    blockAccount: async (publicId) => (await putBlock(publicId)).blocks.map((b) => b.id),
    // BLOCK on a game channel (R1328) goes over the socket, with no answer.
    blockCommander: (accountId) => conn().sendBlock(accountId),

    // -- the camera -------------------------------------------------------
    focus: (x, y) => deps.camera.centerOn(x, y),
    focusCell: (cell) => {
      const [x, y] = cellCenter(cell);
      deps.camera.centerOn(x, y);
    },
    viewLane: (markId, pts) => {
      if (pts.length === 0) return;
      let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      for (const p of pts) {
        x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y);
        x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y);
      }
      deps.camera.fitRect(x0, y0, x1, y1, CHART.pieces.shipping_lanes.fig.view_fill);
      shippingLanes.view(markId);
    },

    // -- the map ----------------------------------------------------------
    setHeatmap: (kind) => {
      // Sending the mode that is already on turns it off again.
      const next = kind === deps.state.heatmap ? HeatmapKind.HEATMAP_KIND_NONE : kind;
      deps.state.setHeatmap(next);
      deps.heatmap.setMode(next);
      // The client draws CURRENTS and DEPTH (R2870): the server stops the
      // field of the mode before them, and sends none for them.
      conn().sendHeatmapMode(heatServerKind(next));
      const u = ui();
      // The heat page of the VIEW panel stays up (round 086 card 10, R1806):
      // the active mode moves its check, and a second press turns it off.
      // A mode the player just picked must arrive with its key, even if the
      // legend was minimized against the mode before it (R218).
      if (next !== HeatmapKind.HEATMAP_KIND_NONE) u.showHeatLegend();
      u.notice(next === HeatmapKind.HEATMAP_KIND_NONE
        ? S.heatmap_off
        : S.heatmap_on({ label: heatMode(next).label }), false);
      u.render(true);
    },

    // -- the empire and the clan ------------------------------------------
    setAutoRebuild: (on) => conn().sendEmpireSetting('auto_rebuild', on),
    // The advisor switch (R1566): the world takes it at once, and the
    // account keeps it for the next season.
    setAdvisor: (on) => { conn().sendEmpireSetting(ADVISOR_KEY, on); storeAdvisor(on); },
    // The welcome card (R1572): the world keeps the mark on the empire.
    closeWelcome: () => conn().sendEmpireSetting(WELCOME_KEY, true),
    warQuery: () => conn().sendWarQuery(),
    productionQuery: () => conn().sendProductionQuery(),
    previewQuery: (building) => conn().sendPreviewQuery(building),
    timelapseQuery: () => conn().sendTimelapseQuery(),
    inboxFetch: () => conn().sendInbox({ fetch: true }),
    inboxRead: (id, all) => conn().sendInbox({ markRead: id, markAll: all }),
    inboxReadMany: (ids) => conn().sendInbox({ markIds: [...ids] }),
    inboxReplace: (msgId) => conn().sendInboxReplace(msgId),
    inboxLedgerQuery: () => conn().sendInboxLedgerQuery(),
    linkUp: () => conn().isOpen(),
    openReplay: (ref) => conn().sendInbox({ fetchReplay: ref }),
    admin: (op, value = 0) => conn().sendAdmin(op, value),

    // Two sockets, two rooms (R225). A clan holds squads in several worlds,
    // so its channel cannot live inside one world: it rides the meta
    // server's own socket. In direct mode there is no such socket, and the
    // CLAN tab already says so — the line is dropped rather than sent to a
    // room that is not there.
    say: (channel, text) => {
      if (channel === ChatChannel.CHAT_CHANNEL_CLAN) {
        deps.clanChat()?.say(text);
        return;
      }
      conn().sendChat(channel, text);
    },

    // -- the way out ------------------------------------------------------
    // Log out (OPTIONS · account, and the START OVER button on a refused
    // join). The session cookie and the per-world resume token are two
    // different things, and both have to go: the cookie names the account,
    // the token names the seat. The reload then lands on the sign-in
    // screen, which is where a signed-out player belongs (R502).
    logout: () => {
      void (async () => {
        try { await authLogout(); } catch { /* the reload is the logout that matters */ }
        clearToken(conn().worldId);
        location.reload();
      })();
    },
    // Back to the hub (R236). It keeps both of the things logout drops: the
    // account session, and this world's resume token, so the hub draws the
    // seat and RESUME comes straight back to it.
    //
    // It is a navigation to the hub address and no longer a reload
    // (R1203). The world goes down first, so the old page does no more
    // work while the next one loads and the server releases the seat at
    // once. `assignWithGesture` carries the press, so the menu music may
    // start on the next page. A throw in the teardown must never keep the
    // player in the world, so the navigation runs whatever it says.
    menu: () => leaveFor(hubAddress(location.href)),
    // ARCADE beside MENU (R1731): the same teardown, onto the arcade page.
    // The session and the seat stay, as they do for MENU.
    arcade: () => leaveFor(ARCADE_HREF),
    // A hub row of the OPTIONS ACCOUNT page (R1731): the same teardown,
    // onto the part of the hub that holds the setting.
    leaveFor,
    // SEA VIEW HERE of the VIEW panel (R2493, seaview.md §4): the same
    // teardown, onto the sea view of this world. A selected fleet is the
    // place and the hull the sea view follows; with none, the centre of the
    // screen. The sea view joins the seat of this tab (seaview.md §1.4), so
    // it opens in this tab and never beside the map. The address this map
    // dials goes into the session store of the tab first, so the sea view
    // dials the socket of the world and not `/ws` of the page (R2494). The
    // link names the mode of the world, so a ticket the sea view asks for
    // after a refused dial names that mode too (R2828).
    seaView: () => {
      const h = deps.state.hello;
      if (!h) return;
      rememberSeaSocket(storeOf('session'), h.worldId, deps.conn().url);
      const sel = deps.state.selection;
      const follow = sel?.kind === 'fleet' ? sel.id : 0;
      const at = (follow > 0 ? deps.fleetAt(follow) : null) ?? deps.camera.centre();
      // The browser BACK button reloads this entry (R2817): `#at` makes that
      // a return from the sea view too, with no arrival (R2975).
      history.replaceState(history.state, '', withAtKnob(location.href, deps.camera.centre()));
      leaveFor(seaViewHref(location.href, h.worldId, { x: at.x, y: at.y, follow }, deps.worldMode?.() ?? ''));
    },

    // -- sound ------------------------------------------------------------
    // The overlay owns the settings blob and writes it — exactly as it does
    // for the display switches — and asks here for it to be applied, so a
    // flipped switch or a dragged slider is heard at once instead of at the
    // next one-second sweep.
    audioApply: () => { deps.audio.applySettings(soundSettings(ui().settings)); deps.pushAudio(); },
    audioOp: (op, arg) => { AUDIO_OPS[op](deps.audio, arg); deps.pushAudio(); },

    // An order the overlay refuses on its own goes through the same door as
    // one the server refuses, so it wears the same buzz and the same toast.
    refuse: (text) => deps.bus.refusal(text),
  };
}
